import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  Trash2,
  GripVertical,
  Loader2,
  Plane,
  Building2,
  Car,
  Utensils,
  MoreHorizontal,
  Sparkles,
  ShoppingBag,
  Heart,
  Gift,
  Music,
  Camera,
  Gamepad2,
  Briefcase,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

interface Service {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  active: boolean;
  sort_order: number;
  welcome_message: string | null;
}

const AVAILABLE_ICONS = [
  { name: "Plane", icon: Plane, label: "Avion" },
  { name: "Building2", icon: Building2, label: "Hôtel" },
  { name: "Car", icon: Car, label: "Voiture" },
  { name: "Utensils", icon: Utensils, label: "Restaurant" },
  { name: "MoreHorizontal", icon: MoreHorizontal, label: "Autre" },
  { name: "Sparkles", icon: Sparkles, label: "Premium" },
  { name: "ShoppingBag", icon: ShoppingBag, label: "Shopping" },
  { name: "Heart", icon: Heart, label: "Bien-être" },
  { name: "Gift", icon: Gift, label: "Cadeaux" },
  { name: "Music", icon: Music, label: "Événements" },
  { name: "Camera", icon: Camera, label: "Photo" },
  { name: "Gamepad2", icon: Gamepad2, label: "Loisirs" },
  { name: "Briefcase", icon: Briefcase, label: "Business" },
];

const getIconComponent = (iconName: string | null) => {
  const found = AVAILABLE_ICONS.find(i => i.name === iconName);
  return found?.icon || MoreHorizontal;
};

export function ServiceManagement() {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formName, setFormName] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formIcon, setFormIcon] = useState("MoreHorizontal");
  const [formActive, setFormActive] = useState(true);
  const [formWelcomeMessage, setFormWelcomeMessage] = useState("");

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    const { data, error } = await supabase
      .from("services")
      .select("*")
      .order("sort_order", { ascending: true });

    if (!error && data) {
      setServices(data as Service[]);
    }
    setLoading(false);
  };

  const openDialog = (service?: Service) => {
    if (service) {
      setEditingService(service);
      setFormName(service.name);
      setFormDescription(service.description || "");
      setFormIcon(service.icon || "MoreHorizontal");
      setFormActive(service.active);
      setFormWelcomeMessage(service.welcome_message || "");
    } else {
      setEditingService(null);
      setFormName("");
      setFormDescription("");
      setFormIcon("MoreHorizontal");
      setFormActive(true);
      setFormWelcomeMessage("");
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formName.trim()) {
      toast.error("Le nom du service est requis");
      return;
    }

    setSaving(true);

    if (editingService) {
      // Update
      const { error } = await supabase
        .from("services")
        .update({
          name: formName.trim(),
          description: formDescription.trim() || null,
          icon: formIcon,
          active: formActive,
          welcome_message: formWelcomeMessage.trim() || null,
        })
        .eq("id", editingService.id);

      if (error) {
        toast.error("Échec de la mise à jour");
      } else {
        toast.success("Service mis à jour");
        setServices(prev =>
          prev.map(s =>
            s.id === editingService.id
              ? { ...s, name: formName.trim(), description: formDescription.trim() || null, icon: formIcon, active: formActive, welcome_message: formWelcomeMessage.trim() || null }
              : s
          )
        );
        setDialogOpen(false);
      }
    } else {
      // Create
      const maxOrder = services.length > 0 ? Math.max(...services.map(s => s.sort_order)) : 0;
      const { data, error } = await supabase
        .from("services")
        .insert({
          name: formName.trim(),
          description: formDescription.trim() || null,
          icon: formIcon,
          active: formActive,
          sort_order: maxOrder + 1,
          welcome_message: formWelcomeMessage.trim() || null,
        })
        .select()
        .single();

      if (error) {
        toast.error("Échec de la création");
      } else {
        toast.success("Service créé");
        setServices(prev => [...prev, data as Service]);
        setDialogOpen(false);
      }
    }

    setSaving(false);
  };

  const handleToggleActive = async (service: Service) => {
    const { error } = await supabase
      .from("services")
      .update({ active: !service.active })
      .eq("id", service.id);

    if (error) {
      toast.error("Échec de la mise à jour");
    } else {
      setServices(prev =>
        prev.map(s => (s.id === service.id ? { ...s, active: !s.active } : s))
      );
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce service ?")) return;

    const { error } = await supabase.from("services").delete().eq("id", id);

    if (error) {
      toast.error("Échec de la suppression");
    } else {
      toast.success("Service supprimé");
      setServices(prev => prev.filter(s => s.id !== id));
    }
  };

  const moveService = async (index: number, direction: "up" | "down") => {
    const newIndex = direction === "up" ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= services.length) return;

    const newServices = [...services];
    [newServices[index], newServices[newIndex]] = [newServices[newIndex], newServices[index]];

    // Update sort_order for both services
    const updates = newServices.map((s, i) => ({ id: s.id, sort_order: i }));
    
    for (const update of updates) {
      await supabase
        .from("services")
        .update({ sort_order: update.sort_order })
        .eq("id", update.id);
    }

    setServices(newServices.map((s, i) => ({ ...s, sort_order: i })));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {services.length} service{services.length > 1 ? "s" : ""} configuré{services.length > 1 ? "s" : ""}
        </p>
        <Button variant="gold" size="sm" onClick={() => openDialog()}>
          <Plus size={16} className="mr-1" />
          Ajouter
        </Button>
      </div>

      {/* Services List */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {services.length === 0 ? (
          <div className="p-8 text-center">
            <Sparkles size={40} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Aucun service configuré</p>
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => openDialog()}>
              Créer un service
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {services.map((service, index) => {
              const IconComponent = getIconComponent(service.icon);
              return (
                <div
                  key={service.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-accent/50 transition-colors"
                >
                  {/* Drag handle / reorder */}
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => moveService(index, "up")}
                      disabled={index === 0}
                      className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
                        <path d="M5 0L10 6H0L5 0Z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => moveService(index, "down")}
                      disabled={index === services.length - 1}
                      className="p-1 text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
                        <path d="M5 6L0 0H10L5 6Z" />
                      </svg>
                    </button>
                  </div>

                  {/* Icon */}
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    <IconComponent size={20} className="text-primary" />
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground text-sm">{service.name}</p>
                    {service.description && (
                      <p className="text-xs text-muted-foreground line-clamp-1">
                        {service.description}
                      </p>
                    )}
                  </div>

                  {/* Status */}
                  <Switch
                    checked={service.active}
                    onCheckedChange={() => handleToggleActive(service)}
                  />

                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openDialog(service)}
                    >
                      <Edit size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(service.id)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif">
              {editingService ? "Modifier le service" : "Nouveau service"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Nom du service</Label>
              <Input
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="Ex: Billets d'avion"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formDescription}
                onChange={e => setFormDescription(e.target.value)}
                placeholder="Ex: Réservation de vols, upgrades, lounges VIP"
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Icône</Label>
              <Select value={formIcon} onValueChange={setFormIcon}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AVAILABLE_ICONS.map(({ name, icon: Icon, label }) => (
                    <SelectItem key={name} value={name}>
                      <div className="flex items-center gap-2">
                        <Icon size={16} />
                        <span>{label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Message de démarrage</Label>
              <Textarea
                value={formWelcomeMessage}
                onChange={e => setFormWelcomeMessage(e.target.value)}
                placeholder="Ex: Bonjour, j'aimerais réserver un billet d'avion..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Message pré-rempli que le client peut envoyer pour démarrer la conversation
              </p>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Service actif</Label>
                <p className="text-xs text-muted-foreground">Visible par les clients</p>
              </div>
              <Switch checked={formActive} onCheckedChange={setFormActive} />
            </div>

            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full"
              variant="gold"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : editingService ? (
                "Mettre à jour"
              ) : (
                "Créer le service"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}